import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { TDR, LANDING_PAGE_PLACEHOLDERS } from 'tdr-common';
import { useTranslation } from 'react-i18next';
import { analytics, LINK_CLICKED_EVENT, MODAL_OPENED_EVENT } from '../../../../helpers/analytics';
import { parseClosedDateDescription } from '../../../../helpers/closedDateConfig';
import { setLang } from '../../../../helpers/setLang';
import { resolveLocalizedText } from '../../../../helpers/resolveLocalizedText';
import RestaurantEmailLink from '../../../buttons/RestaurantEmailLink';
import RestaurantPhoneLink from '../../../buttons/RestaurantPhoneLink';
import { ExternalLink } from '../../actions/external-link/external-link';
import { Logo } from '../../logo/logo';
import { Modal, ModalControlProps } from '../modal';

type UnavailableDateProps = {
	date: DateTime;
	groupSize: number;
	restaurant: TDR.Restaurant;
	unavailableMonth?: string;
	closedResturant?: boolean;
	closedDateDescription?: string;
} & ModalControlProps;

export const UnavailableDate = ({ date, groupSize, restaurant, open, onOpenChange, unavailableMonth, closedResturant, closedDateDescription }: UnavailableDateProps) => {
	const { t, i18n } = useTranslation();
	const customConfig = parseClosedDateDescription(closedDateDescription);
	const altPlatform = restaurant.settings?.alternativePlatform?.vendor;
	const altPlatformDisplayName = TDR.RMSDisplayNameMap[altPlatform];
	const altPlatformHref = restaurant.settings?.alternativePlatform?.href;

	let href = altPlatformHref;
	if (i18n.language !== 'en') {
		href = setLang(href, i18n.language);
	}

	const logoSrc = `../../../../images/poweredBy/${altPlatform}.svg`;

	const contactByPhone = altPlatform === 'phone';
	const contactByEmail = altPlatform === 'email';
	const isCustomAlternativePlatform = altPlatform === 'custom';
	const useAltPlatform = !contactByPhone && !contactByEmail && !isCustomAlternativePlatform;

	const handleThirdPartyRMSClick = () => {
		analytics.track(LINK_CLICKED_EVENT, {
			href: href,
			purpose: 'Redirect to RMS from Unavailable Date Modal',
			platform: altPlatform
		});
	};

	const LogoIcon = !altPlatformDisplayName ? null : <Logo src={logoSrc} alt={altPlatformDisplayName} />;

	const getActionText = () => {
		if (contactByPhone && !!restaurant.phoneNumber) {
			return (
				<p>
					{t('checkout.modals.unavailable_date.action_phone')} <RestaurantPhoneLink phoneNumber={restaurant.phoneNumber} />.
				</p>
			);
		}

		if (contactByEmail && restaurant.contactEmail) {
			return (
				<p>
					{t('checkout.modals.unavailable_date.action_email')} <RestaurantEmailLink email={restaurant?.contactEmail} />.
				</p>
			);
		}

		if (isCustomAlternativePlatform) {
			return (
				<p>
					{t('checkout.modals.unavailable_date.action_platform')}
				</p>
			);
		}

		if (useAltPlatform) {
			return (
				<p>
					{t('checkout.modals.unavailable_date.action_platform')}
					{!altPlatformDisplayName ? (
						'.'
					) : (
						<>
							, {t('checkout.modals.unavailable_date.action_platform_with_name')} <strong>{altPlatformDisplayName}</strong>.
						</>
					)}
				</p>
			);
		}
	};

	useEffect(() => {
		if (open) {
			analytics.track(MODAL_OPENED_EVENT, { title: 'Unavailable Date Modal' });
		}
	}, [open]);

	const getDateInfo = () => {
		if (closedResturant) {
			return '';
		}
		if (unavailableMonth) {
			return t('checkout.modals.unavailable_date.in_month', { month: unavailableMonth });
		}
		if (date) {
			const locale = i18n.language === 'fr' ? 'fr-CA' : 'en';
			const formatted = date.setLocale(locale).toLocaleString({ month: 'long', day: 'numeric' });
			return t('checkout.modals.unavailable_date.on_date', { date: formatted });
		}
		return '';
	};

	const modalTitle = customConfig?.title ||
		(closedResturant ? t('checkout.modals.unavailable_date.title_unavailable') : t('checkout.modals.unavailable_date.title_available'));

	const modalMessage = customConfig?.message ||
		t('checkout.modals.unavailable_date.description', {
			groupSize: groupSize,
			plural: groupSize > 1 ? 's' : '',
			dateInfo: getDateInfo()
		});

	const showCustomCopyCta = customConfig?.cta?.url && customConfig?.cta?.text;
	const showDefaultCTA = !showCustomCopyCta && altPlatformDisplayName && useAltPlatform;

	const handleCustomCTAClick = () => {
		analytics.track(LINK_CLICKED_EVENT, {
			href: customConfig.cta.url,
			purpose: 'Redirect from Custom Unavailable Date Modal',
			customDescription: closedDateDescription
		});
	};

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<Modal.Header closeModal={() => onOpenChange(false)}>
				{modalTitle}
			</Modal.Header>

			<Modal.Body>
				<p>{modalMessage}</p>
				{!customConfig && getActionText()}
			</Modal.Body>
		<Modal.Footer>
			{showCustomCopyCta && (
				<ExternalLink
					fullWidth
					variant='secondary'
					href={customConfig.cta.url}
					aria-label={customConfig.cta.text}
					onClick={handleCustomCTAClick}
				>
					{customConfig.cta.text}
				</ExternalLink>
			)}
			{!showCustomCopyCta && isCustomAlternativePlatform && (
				<ExternalLink
					fullWidth
					variant='secondary'
					href={href}
					aria-label={resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.callToAction, i18n.language) || LANDING_PAGE_PLACEHOLDERS.alternate.callToAction}
					onClick={handleThirdPartyRMSClick}
				>
					{resolveLocalizedText(restaurant.settings?.landingPage?.alternate?.callToAction, i18n.language) || LANDING_PAGE_PLACEHOLDERS.alternate.callToAction}
				</ExternalLink>
			)}
			{showDefaultCTA && (
				<ExternalLink
					fullWidth
					variant='secondary'
					href={href}
					endIcon={LogoIcon}
					aria-label={`Find availability on ${altPlatformDisplayName}`}
					onClick={handleThirdPartyRMSClick}
				>
					{t('checkout.modals.unavailable_date.find_availability')}
				</ExternalLink>
			)}
		</Modal.Footer>
		</Modal>
	);
};
